import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

// Typing cursor blink animation (Tailwind doesn't have blink by default)
const typingCursorStyle = {
	animation: 'socrates-blink 1s infinite',
};
const styleSheet = document.createElement('style');
styleSheet.textContent = `
@keyframes socrates-blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}
`;
if (typeof document !== 'undefined' && !document.getElementById('socrates-blink-style')) {
	styleSheet.id = 'socrates-blink-style';
	document.head.appendChild(styleSheet);
}

const Promotion = () => {
	const [isVisible, setIsVisible] = useState(true);
	const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
	const [hasShownFirstMessage, setHasShownFirstMessage] = useState(false);
	const [displayText, setDisplayText] = useState('');
	const [isTyping, setIsTyping] = useState(false);
	const { t } = useTranslation();

	const messages = useMemo(() => [
        t('promotion.message1'),
    ], [t]);

	const hasUserClosedMessages = useCallback(() => {
		const lastClosed = localStorage.getItem('socrates-messages-closed');
		if (!lastClosed) return false;
		const lastClosedTime = parseInt(lastClosed, 10);
		const hoursSinceClosed = (Date.now() - lastClosedTime) / (1000 * 60 * 60);
		return hoursSinceClosed < 24 * 30;
	}, []);

	useEffect(() => {
		if (hasUserClosedMessages()) return;
		const timer = setTimeout(() => {
			setIsVisible(true);
			setHasShownFirstMessage(true);
		}, 2000);
		return () => clearTimeout(timer);
	}, [hasUserClosedMessages]);

	const typingTimerRef = useRef(null);
	const startTimeRef = useRef(null);

	useEffect(() => {
		if (!isVisible) return;

		const currentMessage = messages[currentMessageIndex];
		setDisplayText('');
		setIsTyping(true);

		const typingSpeed = 50;
		const totalDuration = currentMessage.length * typingSpeed;

		const typeMessage = (timestamp) => {
			if (!startTimeRef.current) startTimeRef.current = timestamp;
			const elapsed = timestamp - startTimeRef.current;
			const progress = Math.min(elapsed / totalDuration, 1);
			const charIndex = Math.floor(progress * currentMessage.length);
			setDisplayText(currentMessage.slice(0, charIndex));
			if (progress < 1) {
				typingTimerRef.current = requestAnimationFrame(typeMessage);
			} else {
				setIsTyping(false);
				startTimeRef.current = null;
			}
		};

		typingTimerRef.current = requestAnimationFrame(typeMessage);
		return () => {
			if (typingTimerRef.current) cancelAnimationFrame(typingTimerRef.current);
			startTimeRef.current = null;
		};
	}, [currentMessageIndex, isVisible, messages]);

	useEffect(() => {
		if (!hasShownFirstMessage) return;
		const currentMessage = messages[currentMessageIndex];
		const typingTime = currentMessage.length * 50;
		const pauseTime = 30000;
		const totalTime = typingTime + pauseTime;
		const timeout = setTimeout(() => {
			setCurrentMessageIndex((prev) =>
				prev === messages.length - 1 ? 0 : prev + 1
			);
		}, totalTime);
		return () => clearTimeout(timeout);
	}, [hasShownFirstMessage, currentMessageIndex, messages]);

	const handleCloseMessage = useCallback(() => {
		setIsVisible(false);
		localStorage.setItem('socrates-messages-closed', Date.now().toString());
	}, []);

	return (

			<div
				className="pointer-events-none flex-shrink-0 z-[1200]"
				aria-live="polite"
			>
				<div
					className={`pointer-events-auto transition-opacity duration-[400ms] ${
						isVisible ? 'opacity-100' : 'opacity-0'
					}`}
				>
					<div className="relative w-64 max-w-[min(90vw,20rem)] rounded-[20px] bg-[#fgfgfg] py-5 pl-5 pr-10 shadow-xl sm:w-72">
					    <div
							className="absolute bottom-5 -left-5 h-0 w-0 border-[10px] border-solid border-transparent border-r-white md:bottom-4 z-[1000]"
							aria-hidden
						/>
						<p
							className="m-0 text-left font-[inherit] text-[15px] font-normal leading-[1.7] tracking-[0.01em] text-[#2c3e50] md:text-sm md:leading-[1.6]"
							style={{
								fontFamily:
									'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							}}
						>
							{displayText}
							{isTyping && (
								<span
									className="inline-block opacity-100"
									style={typingCursorStyle}
								>
									|
								</span>
							)}
						</p>

						<button
							type="button"
							onClick={handleCloseMessage}
							aria-label={t('general.close')}
							className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded bg-transparent text-[#9ca3af] transition hover:bg-black/5 hover:text-[#6b7280] hover:scale-110"
						>
							<i className="ri-close-line text-lg" />
						</button>
					</div>
				</div>
			</div>
	);
};

export default Promotion;
